import React from 'react';
import { useQuery } from 'react-query';
import { 
  UsersIcon, 
  DocumentTextIcon, 
  CurrencyDollarIcon, 
  ExclamationTriangleIcon,
  ArrowTrendingUpIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import { reportAPI, formatCurrency } from '../../services/api';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import StatsCard from '../../components/Dashboard/StatsCard';
import RecentLoans from '../../components/Dashboard/RecentLoans';
import OutstandingLoans from '../../components/Dashboard/OutstandingLoans';
import MonthlyTrend from '../../components/Dashboard/MonthlyTrend';

const Dashboard = () => {
  // Fetch unified dashboard snapshot
  const { data: snapshotData, isLoading: snapshotLoading } = useQuery(
    'dashboard-snapshot',
    () => reportAPI.getDashboardSnapshot(),
    {
      refetchInterval: 60 * 1000, // live-ish sync every 60s
    }
  );

  if (snapshotLoading) {
    return <LoadingSpinner />;
  }

  const snapshot = snapshotData?.data?.data || {};
  const loans = snapshot.loans || {};
  const customers = snapshot.customers || {};
  const collectionsToday = snapshot.collections_today || {};
  const pendingInterestTotal = snapshot.pending_interest_total || 0;

  // Derive dynamic changes from last vs previous month in monthly_trend
  const trend = Array.isArray(snapshot.monthly_trend) ? snapshot.monthly_trend : [];
  const customersTrend = Array.isArray(snapshot.customers_monthly_trend) ? snapshot.customers_monthly_trend : [];
  const last = trend.length >= 1 ? trend[trend.length - 1] : null;
  const prev = trend.length >= 2 ? trend[trend.length - 2] : null;
  const lastCust = customersTrend.length >= 1 ? customersTrend[customersTrend.length - 1] : null;
  const prevCust = customersTrend.length >= 2 ? customersTrend[customersTrend.length - 2] : null;
  const pctChange = (curr, prevVal) => {
    if (prevVal === 0 || prevVal == null || curr == null) return '';
    const diff = ((curr - prevVal) / prevVal) * 100;
    const sign = diff > 0 ? '+' : '';
    return `${sign}${diff.toFixed(0)}%`;
  };
  const changeTypeFrom = (text) => {
    if (!text) return 'neutral';
    return text.startsWith('-') ? 'negative' : 'positive';
  };
  const activeLoansChange = prev && last ? pctChange(last.loan_count, prev.loan_count) : '';
  const disbursedChange = prev && last ? pctChange(last.disbursed_amount, prev.disbursed_amount) : '';
  const customersChange = prevCust && lastCust ? pctChange(lastCust.new_customers, prevCust.new_customers) : '';
  

  const stats = [
    {
      name: 'Total Customers',
      value: customers.total_customers || 0,
      change: customersChange,
      changeType: changeTypeFrom(customersChange),
      icon: UsersIcon,
      color: 'blue',
    },
    {
      name: 'Active Loans',
      value: loans.active_loans || 0,
      change: activeLoansChange,
      changeType: changeTypeFrom(activeLoansChange),
      icon: DocumentTextIcon,
      color: 'green',
    },
    {
      name: 'Total Disbursed',
      value: formatCurrency(loans.total_loan_amount || 0),
      change: disbursedChange,
      changeType: changeTypeFrom(disbursedChange),
      icon: CurrencyDollarIcon,
      color: 'yellow',
    },
    {
      name: 'Outstanding Amount',
      value: formatCurrency(loans.active_loan_amount || 0),
      change: '',
      changeType: 'neutral',
      icon: ExclamationTriangleIcon,
      color: 'red',
    },
    {
      name: 'Collections Today',
      value: formatCurrency(collectionsToday.total_payments_amount || 0),
      change: '',
      changeType: 'neutral',
      icon: ArrowTrendingUpIcon,
      color: 'green',
    },
    {
      name: 'Pending Interest',
      value: formatCurrency(pendingInterestTotal),
      change: '',
      changeType: 'neutral',
      icon: ClockIcon,
      color: 'blue',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold text-primary-700">Dashboard</h1>
        <p className="mt-1 text-sm text-gray-500">
          Overview of your gold loan business
        </p>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {stats.map((stat) => (
          <StatsCard key={stat.name} stat={stat} />
        ))}
      </div>

      

      {/* Charts and tables */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Monthly trend chart */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Monthly Trend</h3>
          <MonthlyTrend data={snapshot.monthly_trend || []} />
        </div>

        {/* Recent loans */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Loans</h3>
          <RecentLoans data={snapshot.recent_loans || []} loading={false} />
        </div>
      </div>

      {/* Outstanding loans table */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Outstanding Loans</h3>
          <p className="mt-1 text-sm text-gray-500">
            Loans requiring attention
          </p>
        </div>
        <OutstandingLoans 
          data={snapshot.outstanding_top || []} 
          loading={false} 
        />
      </div>

      
      
    </div>
  );
};

export default Dashboard;
